import { useEffect, useRef, useCallback } from 'react';
import { Toaster, toast, useToasterStore } from 'react-hot-toast';

const SWIPE_THRESHOLD = 80;

const MAX_VISIBLE_TOASTS = 5;

export default function SwipeableToaster(props: React.ComponentProps<typeof Toaster>) {
  const containerRef = useRef<HTMLDivElement>(null);
  const { toasts: allToasts } = useToasterStore();

  useEffect(() => {
    const visible = allToasts.filter(t => t.visible);
    if (visible.length > MAX_VISIBLE_TOASTS) {
      visible.slice(MAX_VISIBLE_TOASTS).forEach(t => toast.dismiss(t.id));
    }
  }, [allToasts]);
  const touchState = useRef<{ startX: number; startY: number; toastEl: HTMLElement | null }>({
    startX: 0,
    startY: 0,
    toastEl: null,
  });

  const findToastElement = (el: HTMLElement | null): HTMLElement | null => {
    while (el) {
      if (el.getAttribute('data-testid')?.startsWith('toast') || el.getAttribute('role') === 'status') return el;
      // react-hot-toast wraps each toast in a div with specific styles
      if (el.parentElement === containerRef.current?.firstElementChild) return el;
      el = el.parentElement as HTMLElement | null;
    }
    return null;
  };

  const handleTouchStart = useCallback((e: TouchEvent) => {
    const touch = e.touches[0];
    const toastEl = findToastElement(e.target as HTMLElement);
    if (!toastEl) return;
    touchState.current = { startX: touch.clientX, startY: touch.clientY, toastEl };
  }, []);

  const handleTouchMove = useCallback((e: TouchEvent) => {
    const { startX, toastEl } = touchState.current;
    if (!toastEl) return;
    const dx = e.touches[0].clientX - startX;
    // Only apply horizontal transform if swipe is more horizontal than vertical
    const dy = e.touches[0].clientY - touchState.current.startY;
    if (Math.abs(dy) > Math.abs(dx)) return;
    toastEl.style.transform = `translateX(${dx}px)`;
    toastEl.style.opacity = `${Math.max(0, 1 - Math.abs(dx) / 200)}`;
    toastEl.style.transition = 'none';
  }, []);

  const handleTouchEnd = useCallback((e: TouchEvent) => {
    const { startX, toastEl } = touchState.current;
    if (!toastEl) return;
    const dx = e.changedTouches[0].clientX - startX;

    if (Math.abs(dx) > SWIPE_THRESHOLD) {
      // Swipe out animation then dismiss
      const direction = dx > 0 ? 1 : -1;
      toastEl.style.transition = 'transform 0.2s ease-out, opacity 0.2s ease-out';
      toastEl.style.transform = `translateX(${direction * 300}px)`;
      toastEl.style.opacity = '0';

      // Find toast id from the DOM structure and dismiss
      setTimeout(() => {
        // Dismiss all visible toasts since we can't easily get the ID from DOM
        // react-hot-toast assigns IDs internally
        toast.dismiss();
      }, 200);
    } else {
      // Snap back
      toastEl.style.transition = 'transform 0.2s ease-out, opacity 0.2s ease-out';
      toastEl.style.transform = 'translateX(0)';
      toastEl.style.opacity = '1';
    }

    touchState.current = { startX: 0, startY: 0, toastEl: null };
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    container.addEventListener('touchstart', handleTouchStart, { passive: true });
    container.addEventListener('touchmove', handleTouchMove, { passive: true });
    container.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd]);

  return (
    <div ref={containerRef}>
      <Toaster {...props} />
    </div>
  );
}
